import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Modal } from '@fiado/react-kit';
import { handleApiError } from '../../api/errorMapping';
import type {
    UpdateApiUserAccessRequestLocal,
    UpdateTenantUserRequestLocal,
} from '../../api/types/TenantLocal';

const CONTACT_REQUIRED_MESSAGE = 'El contacto técnico no se puede quitar; escribe otro.';

const schema = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'Escribe el nombre del sistema.')
        .max(120, 'Usa máximo 120 caracteres.'),
    technicalContact: z.string().trim().max(200, 'Usa máximo 200 caracteres.'),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    systemName: string;
    technicalContact?: string;
    /** Sin tenant.api_user.manage el contacto técnico no se muestra (su endpoint lo exige). */
    canEditContact: boolean;
    update: (body: UpdateTenantUserRequestLocal) => Promise<unknown>;
    updateApiAccess: (body: UpdateApiUserAccessRequestLocal) => Promise<unknown>;
    onSaved: () => Promise<void> | void;
}

/** Edita un sistema (API): nombre y contacto técnico. Las IPs van en su propio diálogo. */
export function EditApiUserDialog({
    open,
    onOpenChange,
    systemName,
    technicalContact = '',
    canEditContact,
    update,
    updateApiAccess,
    onSaved,
}: Props) {
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { displayName: systemName, technicalContact },
    });

    useEffect(() => {
        if (open) form.reset({ displayName: systemName, technicalContact });
    }, [open, systemName, technicalContact, form]);

    const saveMut = useMutation({
        mutationFn: async (v: FormValues): Promise<void> => {
            const displayName = v.displayName.trim();
            const contact = v.technicalContact.trim();
            if (displayName !== systemName) await update({ displayName });
            if (canEditContact && contact !== technicalContact) {
                await updateApiAccess({ technicalContact: contact });
            }
        },
        onSuccess: async () => {
            toast.success('Sistema actualizado');
            onOpenChange(false);
            await onSaved();
        },
        onError: handleApiError,
    });

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            title={`Editar ${systemName}`}
            size="md"
        >
            <form
                className="space-y-4"
                onSubmit={(e) => {
                    void form.handleSubmit((v) => {
                        // El backend no permite borrar el contacto: solo reemplazarlo.
                        if (canEditContact && technicalContact && !v.technicalContact.trim()) {
                            form.setError('technicalContact', {
                                message: CONTACT_REQUIRED_MESSAGE,
                            });
                            return;
                        }
                        saveMut.mutate(v);
                    })(e);
                }}
            >
                <Input
                    id="edit-api-user-name"
                    label="Nombre del sistema"
                    error={form.formState.errors.displayName?.message}
                    {...form.register('displayName')}
                />
                {canEditContact && (
                    <Input
                        id="edit-api-user-contact"
                        label="Contacto técnico"
                        placeholder="soporte@socio.com"
                        hint="A quién buscar si algo falla con la integración. No recibe correos automáticos."
                        error={form.formState.errors.technicalContact?.message}
                        {...form.register('technicalContact')}
                    />
                )}
                <p className="text-xs text-muted-foreground">
                    Las IPs permitidas se cambian desde “Editar IPs”.
                </p>
                <div className="flex justify-end gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange(false);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button type="submit" isLoading={saveMut.isPending}>
                        Guardar
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
